import React, { useRef, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import "./GestionIa.css";
import { FaCheckCircle } from "react-icons/fa";

interface GestionItem {
  titulo: string;
  descripcion: string;
}

interface AlcanceItem {
  titulo: string;
  descripcion?: string;
}

interface GestionIaProps {
  titulo?: string | null;
  subtitulo?: string | null;
  introText?: string | null;
  gestion: GestionItem[];
  alcance: AlcanceItem[];
}

export default function GestionIa({
  titulo,
  subtitulo,
  introText,
  gestion,
  alcance,
}: GestionIaProps) {
  const cajasRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [animadas, setAnimadas] = useState<number[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number((entry.target as HTMLElement).dataset.idx);
            setAnimadas((prev) => {
              if (!prev.includes(idx)) return [...prev, idx];
              return prev;
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 },
    );

    cajasRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [alcance]);

  const { t } = useTranslation();
  return (
    <>
      <div className="componente-ia">
        {titulo !== null && <h3>{titulo || t("gestion.gestion_title")}</h3>}
        {subtitulo !== null && (
          <h2 className={introText ? "con-intro" : ""}>
            {subtitulo || t("gestion.ia_intro")}
          </h2>
        )}
        {introText && (
          <div className="gestion-ia-intro-parrafo">
            {introText.split("\n").map((line: string, i: number) => (
              <React.Fragment key={i}>
                {line}
                <br />
              </React.Fragment>
            ))}
          </div>
        )}
        <div className="contenedor-ia">
          {Array.isArray(gestion) &&
            gestion.map((item, idx) => (
              <div className="grupo-ia" key={idx}>
                <div className="caja-ia">{item.titulo}</div>
                <motion.p
                  className="descripcion-ia"
                  initial={{ opacity: 0, y: -20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  viewport={{ once: false, amount: 0.3 }}
                  transition={{ duration: 0.6 }}
                >
                  {item.descripcion}
                </motion.p>
              </div>
            ))}
        </div>
      </div>

      <div className="componente-ia2">
        <h3>{t("gestion.alcance_title")}</h3>
        <div className="contenedor-ia2">
          {Array.isArray(alcance) &&
            alcance.map((grupo, idx) => (
              <div className="grupo-ia2" key={idx}>
                <div
                  className="caja-ia2 con-icono-ia"
                  ref={(el) => {
                    cajasRefs.current[idx] = el;
                  }}
                  data-idx={idx}
                  style={
                    {
                      animation: animadas.includes(idx)
                        ? "bgMoveData 3s ease-in-out forwards"
                        : "none",
                    } as React.CSSProperties
                  }
                >
                  <FaCheckCircle className="icono-ia" />
                  <span className="texto-item">{grupo.titulo}</span>
                </div>
                {grupo.descripcion && (
                  <motion.p
                    className="descripcion-ia2"
                    initial={{ opacity: 0, y: -20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    viewport={{ once: false, amount: 0.3 }}
                    transition={{ duration: 0.6 }}
                  >
                    {grupo.descripcion}
                  </motion.p>
                )}
              </div>
            ))}
        </div>
      </div>
    </>
  );
}
